import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Inbox, Plus, Trash2, Sparkles, Copy, Check, ExternalLink,
  Send, Mail, FileDown, Receipt,
} from "lucide-react";
import { toast } from "sonner";

/* ──────────────────────────────────────────────────────────────────────────
   BuyerPOInbox — Mode 2 (buyer-side procurement OS).
   Track POs and SEND them to suppliers via verqo. The supplier doesn't
   need an account — they get a public link to view/acknowledge/ship/invoice.
   This page is purely about POs. Supplier directory lives in /buyer/suppliers.
   ────────────────────────────────────────────────────────────────────── */
const TERMS_OPTS = [
  { value: "prepaid", label: "Prepay" },
  { value: "net14",   label: "Net-14" },
  { value: "net30",   label: "Net-30" },
  { value: "net60",   label: "Net-60" },
  { value: "net90",   label: "Net-90" },
];

const STATUS_META = {
  tracked:      { label: "Drafted",        chip: "bg-secondary text-foreground/60 border border-border" },
  sent:         { label: "Sent to supplier", chip: "bg-blue-50 text-blue-700 border border-blue-200" },
  acknowledged: { label: "Acknowledged",   chip: "bg-violet-50 text-violet-700 border border-violet-200" },
  shipped:      { label: "Shipped",        chip: "bg-amber-50 text-amber-800 border border-amber-200" },
  invoiced:     { label: "Invoice received", chip: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
  paid:         { label: "Paid",           chip: "bg-emerald-50 text-emerald-800 border border-emerald-200" },
};

export default function BuyerPOInbox() {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    supplier_name: "", supplier_email: "", po_number: "",
    total: "", terms: "net30", note: "", send_to_supplier: true,
  });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    return api.get("/buyer/hub/external-pos").then(({ data }) => setPos(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => ({
    total:     pos.length,
    sent:      pos.filter(p => ["sent", "acknowledged", "shipped", "invoiced", "paid"].includes(p.status)).length,
    awaiting:  pos.filter(p => p.status === "sent").length,
    invoiced:  pos.filter(p => p.status === "invoiced").length,
    grand:     pos.reduce((s, p) => s + (p.total || 0), 0),
  }), [pos]);

  const submitPO = async () => {
    if (!form.supplier_name.trim()) return toast.error("Supplier name required");
    if (!form.total || Number(form.total) <= 0) return toast.error("Total must be > 0");
    if (form.send_to_supplier && !form.supplier_email.trim()) {
      return toast.error("Supplier email is required to send");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/buyer/hub/external-pos", {
        supplier_name:    form.supplier_name.trim(),
        supplier_email:   form.supplier_email.trim() || undefined,
        po_number:        form.po_number.trim() || undefined,
        total:            Number(form.total),
        terms:            form.terms,
        note:             form.note.trim() || undefined,
        send_to_supplier: form.send_to_supplier,
      });
      if (form.send_to_supplier && data.public_token) {
        toast.success("PO sent · supplier can view it without an account", {
          action: {
            label: "Copy link",
            onClick: () => copyPublicLink(data.public_token),
          },
        });
      } else {
        toast.success("PO drafted");
      }
      setAdding(false);
      setForm({ supplier_name: "", supplier_email: "", po_number: "", total: "", terms: "net30", note: "", send_to_supplier: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  const copyPublicLink = (token) => {
    const url = `${window.location.origin}/p/po/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Public PO link copied");
  };

  const deletePO = async (id) => {
    if (!confirm("Delete this PO? The supplier link will stop working.")) return;
    await api.delete(`/buyer/hub/external-pos/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="buyer-po-inbox">
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50 inline-flex items-center gap-1.5">
            <Inbox className="w-3 h-3"/>verqo Hub · Universal PO Inbox
          </div>
          <h1 className="display text-2xl mt-1 tracking-tight">Send a PO. Even to suppliers off verqo.</h1>
          <p className="text-xs text-foreground/55 mt-1 max-w-xl">
            Suppliers receive a branded PO link they can view, acknowledge, ship, and invoice — without needing an account.
          </p>
        </div>
        <Button onClick={() => setAdding(true)} data-testid="add-external-po-btn"
          className="rounded-full h-10 px-4 bg-foreground text-background hover:bg-foreground/90">
          <Plus className="w-4 h-4 mr-1.5"/>Create PO
        </Button>
      </div>

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="POs in inbox"      value={totals.total}/>
          <Kpi label="Total value"       value={money(totals.grand)} big/>
          <Kpi label="Awaiting supplier" value={totals.awaiting}/>
          <Kpi label="Invoices received" value={totals.invoiced} accent={totals.invoiced > 0 ? "emerald" : undefined}/>
        </div>

        <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="po-list">
          {loading && <div className="p-12 text-center text-sm text-foreground/55">Loading…</div>}
          {!loading && pos.length === 0 && (
            <div className="p-12 text-center text-sm text-foreground/55" data-testid="po-empty">
              <Inbox className="w-8 h-8 mx-auto mb-3 text-foreground/30"/>
              <div className="display text-lg">Send your first PO via verqo</div>
              <div className="text-xs mt-1 max-w-md mx-auto">
                Works with any supplier — even off-platform. They view, acknowledge, ship, and invoice without signing up.
              </div>
              <Button onClick={() => setAdding(true)} className="mt-4 rounded-full bg-foreground text-background hover:bg-foreground/90">
                <Plus className="w-4 h-4 mr-1.5"/>Create PO
              </Button>
            </div>
          )}
          {!loading && pos.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left text-xs uppercase tracking-widest text-foreground/60">
                <tr>
                  <th className="px-5 py-3">PO</th>
                  <th className="px-5 py-3">Supplier</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Total</th>
                  <th className="px-5 py-3">Terms</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pos.map(p => {
                  const meta = STATUS_META[p.status] || STATUS_META.tracked;
                  const hasLink = p.public_token && p.status !== "tracked";
                  const supLink = p.public_token ? `${window.location.origin}/p/po/${p.public_token}` : null;
                  return (
                    <tr key={p.id} className="hover:bg-secondary/30" data-testid={`po-row-${p.id}`}>
                      <td className="px-5 py-3">
                        <div className="mono text-xs">{p.po_number || "—"}</div>
                        {p.claimed_seller_id && (
                          <div className="text-[10px] mono text-emerald-700 mt-0.5 inline-flex items-center gap-1"><Check className="w-2.5 h-2.5"/>Linked to verqo</div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="font-medium">{p.supplier_name}</div>
                        {p.supplier_email && <div className="text-xs text-foreground/55">{p.supplier_email}</div>}
                      </td>
                      <td className="px-5 py-3"><span className={`chip text-[11px] ${meta.chip}`}>{meta.label}</span></td>
                      <td className="px-5 py-3 display">{money(p.total)}</td>
                      <td className="px-5 py-3"><span className="chip bg-secondary text-[11px]">{p.terms.toUpperCase()}</span></td>
                      <td className="px-5 py-3 text-right space-x-1">
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/buyer/hub/external-pos/${p.id}/pdf`}
                          target="_blank" rel="noopener noreferrer"
                          data-testid={`download-pdf-${p.id}`}
                          title="Download branded PO PDF"
                          className="text-[11px] inline-flex items-center gap-1 rounded-full border border-border px-2.5 h-7 hover:border-foreground/30">
                          <FileDown className="w-3 h-3"/>PDF
                        </a>
                        {p.supplier_invoice_id && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/buyer/hub/external-invoices/${p.supplier_invoice_id}/file`}
                            target="_blank" rel="noopener noreferrer"
                            data-testid={`view-invoice-${p.id}`}
                            title="View supplier-uploaded invoice"
                            className="text-[11px] inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 h-7 hover:bg-emerald-100">
                            <Receipt className="w-3 h-3"/>Invoice
                          </a>
                        )}
                        {hasLink && (
                          <>
                            <button onClick={() => copyPublicLink(p.public_token)} data-testid={`copy-link-${p.id}`}
                              title="Copy public PO link"
                              className="text-[11px] inline-flex items-center gap-1 rounded-full border border-border px-2.5 h-7 hover:border-foreground/30">
                              <Copy className="w-3 h-3"/>Copy
                            </button>
                            <a href={supLink} target="_blank" rel="noopener noreferrer" data-testid={`open-link-${p.id}`}
                              className="text-[11px] inline-flex items-center gap-1 rounded-full border border-border px-2.5 h-7 hover:border-foreground/30">
                              <ExternalLink className="w-3 h-3"/>Open
                            </a>
                          </>
                        )}
                        <button onClick={() => deletePO(p.id)} data-testid={`po-delete-${p.id}`}
                          className="text-foreground/40 hover:text-rose-600 p-1">
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Strategic positioning footer */}
        <div className="rounded-2xl bg-secondary/30 border border-border p-4 flex items-start gap-3 text-xs text-foreground/65">
          <Sparkles className="w-4 h-4 text-foreground/50 shrink-0 mt-0.5"/>
          <div>
            <span className="font-medium text-foreground">Run wholesale ops here. With or without your suppliers. </span>
            Every PO sent through verqo becomes a permanent record — even if your supplier never signs up. When they do (one click from the public PO link), tracking auto-syncs and payments unlock instantly. Your <a href="/buyer/suppliers" className="underline hover:no-underline">supplier directory</a> shows everyone you work with, on or off verqo.
          </div>
        </div>
      </div>

      {/* Create-PO dialog */}
      <Dialog open={adding} onOpenChange={(v) => !v && setAdding(false)}>
        <DialogContent className="max-w-lg" data-testid="add-po-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">Create a PO</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/55 -mt-1">
            We'll generate a public link your supplier can use to acknowledge, ship, and invoice. They never need an account.
          </p>

          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Supplier name *">
                <Input value={form.supplier_name} onChange={e => setForm({...form, supplier_name: e.target.value})}
                  placeholder="Acme Cosmetics Co." data-testid="po-supplier-name"/>
              </Field>
              <Field label={form.send_to_supplier ? "Supplier email *" : "Supplier email"}>
                <Input type="email" value={form.supplier_email}
                  onChange={e => setForm({...form, supplier_email: e.target.value})}
                  placeholder="orders@acme.example" data-testid="po-supplier-email"/>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="PO number">
                <Input value={form.po_number} onChange={e => setForm({...form, po_number: e.target.value})}
                  placeholder="APEX-PO-001" data-testid="po-number"/>
              </Field>
              <Field label="Total *">
                <Input type="number" min="0" step="0.01" value={form.total}
                  onChange={e => setForm({...form, total: e.target.value})}
                  placeholder="4500.00" data-testid="po-total"/>
              </Field>
              <Field label="Terms">
                <Select value={form.terms} onValueChange={v => setForm({...form, terms: v})}>
                  <SelectTrigger data-testid="po-terms"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {TERMS_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Note">
              <Textarea rows={2} value={form.note} onChange={e => setForm({...form, note: e.target.value})}
                placeholder="Internal note for your records." data-testid="po-note"/>
            </Field>

            {/* Send toggle */}
            <label className="flex items-center gap-3 rounded-xl border border-border p-3 cursor-pointer hover:bg-secondary/30 transition" data-testid="po-send-toggle">
              <input type="checkbox" checked={form.send_to_supplier}
                onChange={e => setForm({...form, send_to_supplier: e.target.checked})}
                className="w-4 h-4 accent-foreground"/>
              <div className="text-sm">
                <div className="font-medium inline-flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5"/>Send to supplier
                </div>
                <div className="text-xs text-foreground/55">
                  Generates a public PO link. Supplier can view, acknowledge, attach tracking, and upload an invoice — no account needed.
                </div>
              </div>
            </label>
          </div>

          <div className="mt-4 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setAdding(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submitPO} disabled={submitting} data-testid="po-submit"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90">
              {submitting ? "Saving…" : (form.send_to_supplier ? <><Mail className="w-4 h-4 mr-1.5"/>Send PO</> : "Save draft")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, big, accent }) {
  const ringMap = { violet: "ring-violet-200", emerald: "ring-emerald-200" };
  const colorMap = { emerald: "text-emerald-700" };
  return (
    <div className={`rounded-2xl border border-border bg-white p-5 ${accent ? `ring-1 ${ringMap[accent]}` : ""}`}>
      <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{label}</div>
      <div className={`display ${big ? "text-3xl" : "text-2xl"} mt-2 tracking-tight ${accent ? colorMap[accent] : ""}`}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
